/**
 * Fonctions utilitaires pour formater et gérer les adresses des bénéficiaires
 */

/**
 * Formate une adresse (chaîne ou objet) en chaîne lisible
 * @param {string|object} address - L'adresse à formater
 * @returns {string} L'adresse formatée
 */
export const formatAddress = (address) => {
  if (!address) return 'Adresse non spécifiée';
  
  if (typeof address === 'string') {
    return address.trim();
  }
  
  if (typeof address === 'object' && address !== null) {
    const { street, city, postalCode, country, addressNumber } = address;
    const parts = [];
    
    if (street) parts.push(street);
    if (addressNumber) parts.push(`Appt ${addressNumber}`);
    if (postalCode) parts.push(postalCode);
    if (city) parts.push(city);
    if (country) parts.push(country);
    
    return parts.join(', ');
  }
  
  return 'Adresse non spécifiée';
};

/**
 * Génère les URLs de navigation pour une adresse
 * @param {string} address - L'adresse à utiliser pour la navigation
 * @param {string} addressNumber - Numéro d'appartement optionnel
 * @returns {object} URLs pour Waze, Google Maps et Apple Maps
 */
export const getNavigationLinks = (address, addressNumber = '') => {
  const fullAddress = addressNumber ? `${address}, ${addressNumber}` : address;
  const encodedAddress = encodeURIComponent(fullAddress);

  // Détecter la plateforme pour optimiser les liens
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);

  // Pour iOS, utiliser les schémas d'URL natifs avec fallback vers les URLs web
  // Pour Android, utiliser les URLs qui lancent directement la navigation
  const wazeUrl = isIOS
    ? `waze://?q=${encodedAddress}&navigate=yes`
    : `https://waze.com/ul?q=${encodedAddress}&navigate=yes`;

  const googleMapsUrl = isIOS
    ? `comgooglemaps://?daddr=${encodedAddress}&directionsmode=driving`
    : isAndroid
    ? `google.navigation:q=${encodedAddress}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`;

  // Apple Maps avec lancement direct de la navigation
  const appleMapsUrl = isIOS
    ? `maps://?daddr=${encodedAddress}&dirflg=d`
    : `https://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`;

  return {
    waze: wazeUrl,
    googleMaps: googleMapsUrl,
    appleMaps: appleMapsUrl,
    // URLs de fallback pour iOS si les applications ne sont pas installées
    wazeFallback: `https://waze.com/ul?q=${encodedAddress}&navigate=yes`,
    googleMapsFallback: `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`
  };
};

/**
 * Vérifie si une adresse est valide pour la navigation
 * @param {string} address - L'adresse à vérifier
 * @returns {boolean} True si l'adresse est valide
 */
export const isValidAddress = (address) => {
  if (!address) return false;
  const formatted = formatAddress(address);
  return formatted !== 'Adresse non spécifiée' && formatted.length > 5;
};

/**
 * Extrait les composants d'une adresse structurée
 * @param {string} address - L'adresse à analyser
 * @returns {object} Composants de l'adresse
 */
export const parseAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return { street: '', city: '', postalCode: '', country: '' };
  }
  
  // Tentative d'analyse simple de l'adresse
  const parts = address.split(',');
  
  if (parts.length >= 2) {
    return {
      street: parts[0].trim(),
      city: parts[1].trim(),
      postalCode: parts.length > 2 ? parts[2].trim() : '',
      country: parts.length > 3 ? parts[3].trim() : ''
    };
  }
  
  return { street: address.trim(), city: '', postalCode: '', country: '' };
};

/**
 * Normalise une adresse pour la comparaison ou le stockage
 * @param {string} address - L'adresse à normaliser
 * @returns {string} Adresse normalisée
 */
export const normalizeAddress = (address) => {
  if (!address) return '';
  
  const formatted = formatAddress(address);
  return formatted
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Compare deux adresses pour l'égalité (ignorant la casse et les espaces)
 * @param {string} address1 - Première adresse
 * @param {string} address2 - Deuxième adresse
 * @returns {boolean} True si les adresses sont équivalentes
 */
export const areAddressesEqual = (address1, address2) => {
  const normalized1 = normalizeAddress(address1);
  const normalized2 = normalizeAddress(address2);
  return normalized1 === normalized2;
};
